import { Event } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import { FLOW, SCRIPT_URLS } from './FormPaypal.constants';
import { L10N } from './FormPaypal.l10n';
import { loadScript } from '../../../../../helpers/loadScript';
import { trackError } from '../../../../../helpers/trackError';
import { EVENT } from '../../../../helpers';

export const FormPaypal = ({ response, onClose = () => {} }) => {
  const { locale, translate } = useLocale();

  const [ready, setReady] = useState(false);

  const fail = (reason) => {
    trackError('PayPal', reason?.message || 'PayPal SDK failed to load');
    onClose();
    Event.publish(EVENT.NOTIFICATION, {
      error: true,
      defaultMessage: translate(L10N.NOTIFICATION_ERROR),
    });
  };

  useEffect(() => {
    if (!response) return;

    const { configuration: { environment } = {} } = response;
    const src = SCRIPT_URLS[environment];
    if (!src) return fail(new Error(`Unknown PayPal environment: ${environment}`));

    const id = `paypal-${environment}`;
    let mounted = true;
    const handleLoad = () => mounted && setReady(true);

    (async () => {
      try {
        await loadScript({
          domain: 'payments',
          id,
          src,
          onLoad: handleLoad,
        });

        if (window.paypal?.createInstance) handleLoad();
      } catch (nextError) {
        document.getElementById(`payments-${id}`)?.remove();
        if (mounted) fail(nextError);
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  useEffect(() => {
    if (!response || !ready) return;

    let active = true;

    (async () => {
      try {
        const {
          configuration: { clientId, flow, amount, currencyCode, returnUrl, paymentTransactionId, ...rest } = {},
        } = response || {};
        const vault = flow === FLOW.VAULT;
        const tokenKey = vault ? 'vaultSetupToken' : 'orderId';
        const token = rest[tokenKey];

        const sdk = await window.paypal.createInstance({
          clientId,
          components: ['paypal-payments'],
          pageType: 'checkout',
          locale,
        });

        const eligible = await sdk.findEligibleMethods(amount ? { amount, currencyCode } : { currencyCode });

        if (!eligible.isEligible('paypal')) throw new Error('PayPal is unavailable');

        const session = sdk[vault ? 'createPayPalSavePaymentSession' : 'createPayPalOneTimePaymentSession']({
          onApprove: (result) => {
            if (!active) return;

            active = false;
            if (result?.[tokenKey] !== token) return fail(new Error('PayPal order does not match configuration'));

            try {
              const destination = new URL(returnUrl);
              if (destination.protocol !== 'https:') throw new Error('PayPal return url must use https');
              destination.searchParams.set(tokenKey, token);
              if (result.payerId) destination.searchParams.set('payerId', result.payerId);
              if (paymentTransactionId) destination.searchParams.set('paymentTransactionId', paymentTransactionId);

              window.location.href = destination.href;
            } catch (reason) {
              fail(reason);
            }
          },
          onCancel: () => {
            if (!active) return;

            active = false;
            onClose();
          },
          onError: (reason) => {
            if (!active) return;

            active = false;
            fail(reason);
          },
        });
        const started = session.start({ presentationMode: 'modal' }, Promise.resolve({ [tokenKey]: token }));
        await started;
      } catch (reason) {
        if (!active) return;

        active = false;
        fail(reason);
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, response]);

  return null;
};

FormPaypal.displayName = 'Mirai:Payments:ButtonPayment:FormPaypal';

FormPaypal.propTypes = {
  response: PropTypes.shape({
    configuration: PropTypes.shape({
      environment: PropTypes.oneOf(Object.keys(SCRIPT_URLS)).isRequired,
      clientId: PropTypes.string.isRequired,
      flow: PropTypes.oneOf(Object.values(FLOW)),
      orderId: PropTypes.string,
      vaultSetupToken: PropTypes.string,
      amount: PropTypes.string,
      currencyCode: PropTypes.string.isRequired,
      returnUrl: PropTypes.string.isRequired,
      paymentTransactionId: PropTypes.string,
    }).isRequired,
  }).isRequired,
  onClose: PropTypes.func,
};
